import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIsGetStartedPageEnabled } from '@jira/servicedesk__customer-service-get-started-customer-experience/src/useIsGetStartedPageEnabled.tsx';
import { JCSOfferingEdition } from '@jira/servicedesk__customer-service-offering-edition/src/JCSOfferingEdition.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import CompassIcon from '@atlaskit/icon/core/compass';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import FieldCheckboxGroupIcon from '@atlaskit/icon/core/field-checkbox-group';
import PageIcon from '@atlaskit/icon/core/page';
import PaintPaletteIcon from '@atlaskit/icon/core/paint-palette';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import PremiumIcon from '@atlaskit/icon/core/premium';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import NotificationIcon from '@atlaskit/icon/core/notification';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { CUSTOM_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_THREE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { HelpHelpCenterAri } from '@atlassian/ari/help/help-center';
import { NavCustomIcon } from './NavCustomIcon.tsx';
import { ChannelsExpandableMenuItem } from './ChannelsExpandableMenuItem.tsx';
import type { CustomerExperienceMenuContent_helpCenter$key } from './__generated__/CustomerExperienceMenuContent_helpCenter.graphql';

const MENU_ID_CUSTOMER_EXPERIENCE_PREFIX = 'customer-experiences-';

type Props = {
	helpCenterFragment: CustomerExperienceMenuContent_helpCenter$key;
};

export const CustomerExperienceMenuContent = ({
	helpCenterFragment,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const helpCenter = useFragment(
		graphql`
			fragment CustomerExperienceMenuContent_helpCenter on HelpCenter {
				id @required(action: THROW)
				name {
					default
				}
				hoistedProjectKey
			}
		`,
		helpCenterFragment,
	);

	const helpCenterId = HelpHelpCenterAri.parse(helpCenter.id).helpCenterId;
	const projectKey = helpCenter.hoistedProjectKey || '';
	const experienceName = helpCenter.name?.default || formatMessage(defaultExperienceName);

	const [, isGetStartedPageEnabled] = useIsGetStartedPageEnabled({
		matchParams: { projectKey },
	});

	if (!helpCenterId || !projectKey) {
		return null;
	}

	const handleItemClick = (itemType: string) => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'navigationItem',
			actionSubjectId: `customerExperience${itemType}`,
			attributes: {
				level: LEVEL_THREE,
				componentType: CUSTOM_ITEM,
				experienceId: helpCenterId,
				itemType,
			},
		});
	};

	const baseUrl = fg('csm_appification_standalone_navigation')
		? `/jira/customer-service-management/experiences/${projectKey}/settings/${helpCenterId}`
		: `/jira/helpcenter/projects/${projectKey}/settings/${helpCenterId}`;

	const menuId = `${MENU_ID_CUSTOMER_EXPERIENCE_PREFIX}${projectKey}`;

	return (
		<Nav4ExpandableMenuItem
			key={helpCenter.id}
			menuId={menuId}
			testId={`customer-experience-expandable-${projectKey.toLowerCase()}`}
		>
			<ExpandableMenuItemTrigger
				elemBefore={<NavCustomIcon helpCenterId={helpCenterId} />}
				testId={`customer-experience-trigger-${projectKey.toLowerCase()}`}
				interactionName="nav4-sidebar-customer-experience"
			>
				{experienceName}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{isGetStartedPageEnabled && (
					<Nav4MenuLinkItem
						href={`${baseUrl}/get-started`}
						menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'get-started')}
						elemBefore={<CompassIcon label="" color="currentColor" />}
						onClick={() => handleItemClick('GetStarted')}
						testId={`customer-experience-menu-get-started-${projectKey.toLowerCase()}`}
					>
						{formatMessage(getStartedItemMessage)}
					</Nav4MenuLinkItem>
				)}

				<Nav4MenuLinkItem
					href={`${baseUrl}/details`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'details')}
					elemBefore={<AlignTextLeftIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('Details')}
					testId={`customer-experience-menu-details-${projectKey.toLowerCase()}`}
				>
					{formatMessage(overviewItemMessage)}
				</Nav4MenuLinkItem>

				<Nav4MenuLinkItem
					href={`${baseUrl}/forms`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'forms')}
					elemBefore={<FieldCheckboxGroupIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('Forms')}
					testId={`customer-experience-menu-forms-${projectKey.toLowerCase()}`}
				>
					{formatMessage(formsItemMessage)}
				</Nav4MenuLinkItem>

				<Nav4MenuLinkItem
					href={`${baseUrl}/articles`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'articles')}
					elemBefore={<PageIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('Articles')}
					testId={`customer-experience-menu-articles-${projectKey.toLowerCase()}`}
				>
					{formatMessage(articlesItemMessage)}
				</Nav4MenuLinkItem>

				{fg('jcs-global-branding') && (
					<Nav4MenuLinkItem
						href={`${baseUrl}/branding`}
						menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'branding')}
						elemBefore={<PaintPaletteIcon label="" color="currentColor" />}
						onClick={() => handleItemClick('Branding')}
						testId={`customer-experience-menu-branding-${projectKey.toLowerCase()}`}
					>
						{formatMessage(brandingItemMessage)}
					</Nav4MenuLinkItem>
				)}

				{fg('csm-omni-channel-live-chat-in-widget') && (
					<JCSOfferingEdition>
						<Nav4MenuLinkItem
							href={`${baseUrl}/chat`}
							menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'chat')}
							elemBefore={<AiChatIcon label="" color="currentColor" />}
							onClick={() => handleItemClick('Chat')}
							testId={`customer-experience-menu-chat-${projectKey.toLowerCase()}`}
							elemAfter={
								!fg('csm-omni-hide-beta-lozenge') ? (
									<Lozenge appearance="discovery">{formatMessage(betaLozengeMessage)}</Lozenge>
								) : undefined
							}
						>
							{formatMessage(chatItemMessage)}
						</Nav4MenuLinkItem>
					</JCSOfferingEdition>
				)}

				<ChannelsExpandableMenuItem
					helpCenterId={helpCenterId}
					projectKey={projectKey}
					baseUrl={baseUrl}
					onItemClick={handleItemClick}
				/>

				<Nav4MenuLinkItem
					href={`${baseUrl}/customer-access`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'customer-access')}
					elemBefore={<PersonAddIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('CustomerAccess')}
					testId={`customer-experience-menu-customer-access-${projectKey.toLowerCase()}`}
				>
					{formatMessage(customerAccessItemMessage)}
				</Nav4MenuLinkItem>

				<Nav4MenuLinkItem
					href={`${baseUrl}/agent`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'agent')}
					elemBefore={<PremiumIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('Agent')}
					testId={`customer-experience-menu-agent-${projectKey.toLowerCase()}`}
				>
					{formatMessage(aiAgentItemMessage)}
				</Nav4MenuLinkItem>

				<Nav4MenuLinkItem
					href={`${baseUrl}/customer-notifications`}
					menuId={ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'customer-notifications')}
					elemBefore={<NotificationIcon label="" color="currentColor" />}
					onClick={() => handleItemClick('CustomerNotifications')}
					testId={`customer-experience-menu-customer-notifications-${projectKey.toLowerCase()}`}
				>
					{formatMessage(customerNotificationsItemMessage)}
				</Nav4MenuLinkItem>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
};

const getStartedItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.get-started-item',
	defaultMessage: 'Get started',
	description: 'Get started link item in customer experience menu',
});

const defaultExperienceName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.fallback-customer-experience-name',
	defaultMessage: 'Customer experience',
	description: 'Fallback name for a customer experience when its configured name is empty',
});

const overviewItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.overview-item',
	defaultMessage: 'Overview',
	description: 'Overview link item in customer experience menu',
});

const formsItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.forms-item',
	defaultMessage: 'Forms',
	description: 'Forms link item in customer experience menu',
});

const articlesItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.articles-item',
	defaultMessage: 'Articles',
	description: 'Articles link item in customer experience menu',
});

const brandingItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.branding-item',
	defaultMessage: 'Branding',
	description: 'Branding link item in customer experience menu',
});

const chatItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.chat-item',
	defaultMessage: 'Chat',
	description: 'Chat link item in customer experience menu',
});

const customerAccessItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.customer-access-item',
	defaultMessage: 'Customer access',
	description: 'Customer access link item in customer experience menu',
});

const aiAgentItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.ai-agent-item',
	defaultMessage: 'AI agent',
	description: 'AI agent link item in customer experience menu',
});

const customerNotificationsItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.customer-notifications-item',
	defaultMessage: 'Customer notifications',
	description: 'Customer notifications link item in customer experience menu',
});

const betaLozengeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.customer-experience-menu.beta-lozenge',
	defaultMessage: 'Beta',
	description: 'Beta lozenge for chat menu item in customer experience menu',
});
